import { PaginationParameters, PaginationResult, toQueryParameters } from "./Pagination";
import { CallResult, doApiDelete, doApiGet, doApiPatch, doApiPost } from "./Requests";
import { TaxFreeBenefitUserType } from "../types/taxFreeBenefitUsersType";
import { CreateEmployeeType, EmployeeUserType, SingleEmployeeType } from "../types/employeeType";

export interface PaginatedEmployeeWithIdsResult<T> extends PaginationResult<T> {
  totalIds: string[];
}

export const employeesApi = {
  getAccountManagementEmployees: async (
    pagination: PaginationParameters,
    groupIds: number[] = []
  ): Promise<CallResult<PaginationResult<EmployeeUserType>>> => {
    return await doApiGet<PaginationResult<EmployeeUserType>>(
      `/api/companies/current/accountmanagement/employees?${toQueryParameters(pagination)}${groupIds.map(id => `&groupIds=${id}`).join('')}`
    );
  },

  getAccountManagementEmployeesByIds: async (
    pagination: PaginationParameters,
    userData: string[]
  ): Promise<CallResult<PaginatedEmployeeWithIdsResult<EmployeeUserType>>> => {
    return await doApiPost(`/api/companies/current/accountmanagement/employeesbyids?${toQueryParameters(pagination)}`, userData);
  },

  getCompanyEmployees: async (): Promise<CallResult<EmployeeUserType[]>> => {
    return await doApiGet<EmployeeUserType[]>("/api/companies/current/employees");
  },
  
  getServiceModuleEmployees: async (serviceModuleId: number): Promise<CallResult<EmployeeUserType[]>> => {
    return await doApiGet<EmployeeUserType[]>(`/api/companies/current/servicemodules/${serviceModuleId}/employees`);
  },

  getEmployeeById: async (employeeId: string): Promise<CallResult<SingleEmployeeType>> => {
    return await doApiGet<SingleEmployeeType>(`/api/companies/current/employees/${employeeId}`);
  },

  createEmployee: async (
    userData: CreateEmployeeType[]
  ): Promise<CallResult<EmployeeUserType[]>> => {
    return await doApiPost("/api/companies/current/employees", userData);
  },

  editEmployee: async (
    id: string,
    userData: Partial<EmployeeUserType>
  ): Promise<CallResult<EmployeeUserType>> => {
    return await doApiPatch(`/api/employee/${id}/accountmanagement`, userData);
  },

  deleteEmployee: async (
    userId: string
  ): Promise<CallResult<null>> => {
    return await doApiDelete(`/api/companies/current/employees/${userId}`);
  },

  deleteEmployeePassword: async (employeeId: string): Promise<CallResult<null>> => {
    return await doApiDelete(`/api/companies/current/employees/${employeeId}/password`);
  },

  getEmployeeServiceModules: async (
    employeeId: string
  ): Promise<CallResult<number[]>> => {
    return await doApiGet<number[]>(
      `/api/companies/current/employees/${employeeId}/serviceModules`
    );
  },

  patchEmployeeServiceModules: async (
    employeeId: string,
    moduleIds: number[]
  ): Promise<CallResult<null>> => {
    return await doApiPatch(`/api/companies/current/employees/${employeeId}/serviceModules`, moduleIds);
  },

  getEmployeeActionModules: async (
    employeeId: string
  ): Promise<CallResult<number[]>> => {
    return await doApiGet<number[]>(
      `/api/companies/current/employees/${employeeId}/actionModules`
    );
  },

  patchEmployeeActionModules: async (
    employeeId: string,
    moduleIds: number[]
  ): Promise<CallResult<null>> => {
    return await doApiPatch(`/api/companies/current/employees/${employeeId}/actionModules`, moduleIds);
  },

  getEmployeeShopModules: async (
    employeeId: string
  ): Promise<CallResult<number[]>> => {
    return await doApiGet<number[]>(
      `/api/companies/current/employees/${employeeId}/shopModules`
    );
  },

  patchEmployeeShopModules: async (
    employeeId: string,
    moduleIds: number[]
  ): Promise<CallResult<null>> => {
    return await doApiPatch(`/api/companies/current/employees/${employeeId}/shopModules`, moduleIds);
  },

  bookTransactionForEmployee: async (
    employeeId: string
  ): Promise<CallResult<void>> => {
    return await doApiPost<null, void>(
      `/api/companies/current/employees/${employeeId}/transactions/book`,
      null
    );
  },
};
